/**
 * PhysicsEngine类 - 物理模拟引擎
 * 
 * 职责:
 * - 应用重力加速度（9.8单位/秒²）
 * - 应用空气阻力（系数0.98-0.995）
 * - 更新粒子速度和位置
 * - 使用Euler积分进行物理模拟
 * 
 * 验证需求: Requirements 4.5
 */
class PhysicsEngine {
  constructor() {
    // 重力加速度（向下）
    this.gravity = 9.8;
    
    // 空气阻力系数（每帧）
    this.airResistance = 0.99;
  }

  /**
   * 应用重力到粒子
   * @param {Object} particle - 粒子对象
   * @param {number} deltaTime - 时间增量（秒）
   */
  applyGravity(particle, deltaTime) {
    if (!particle || !particle.velocity) return;
    
    // 向下的重力加速度
    particle.velocity.y -= this.gravity * deltaTime;
  }

  /**
   * 应用空气阻力到粒子
   * @param {Object} particle - 粒子对象
   * @param {number} deltaTime - 时间增量（秒）
   */
  applyAirResistance(particle, deltaTime) {
    if (!particle || !particle.velocity) return;
    
    // 空气阻力导致速度衰减
    const resistance = Math.pow(this.airResistance, deltaTime * 60); // 标准化到60fps
    particle.velocity.x *= resistance;
    particle.velocity.y *= resistance;
    particle.velocity.z *= resistance;
  }

  /**
   * 更新粒子速度
   * @param {Object} particle - 粒子对象
   * @param {number} deltaTime - 时间增量（秒）
   */
  updateVelocity(particle, deltaTime) {
    if (!particle || !particle.velocity || !particle.acceleration) return;
    
    // 应用加速度到速度（Euler积分）
    particle.velocity.x += particle.acceleration.x * deltaTime;
    particle.velocity.y += particle.acceleration.y * deltaTime;
    particle.velocity.z += particle.acceleration.z * deltaTime;
  }

  /**
   * 更新粒子位置
   * @param {Object} particle - 粒子对象
   * @param {number} deltaTime - 时间增量（秒）
   */
  updatePosition(particle, deltaTime) {
    if (!particle || !particle.position || !particle.velocity) return;
    
    // 应用速度到位置（Euler积分）
    particle.position.x += particle.velocity.x * deltaTime;
    particle.position.y += particle.velocity.y * deltaTime;
    particle.position.z += particle.velocity.z * deltaTime;
  }

  /**
   * 完整的物理更新（组合所有物理效果）
   * @param {Object} particle - 粒子对象
   * @param {number} deltaTime - 时间增量（秒）
   */
  update(particle, deltaTime) {
    if (!particle || !particle.active) return;
    
    // 应用物理效果
    this.applyGravity(particle, deltaTime);
    this.applyAirResistance(particle, deltaTime);
    this.updateVelocity(particle, deltaTime);
    this.updatePosition(particle, deltaTime);
    
    // 检查粒子是否落到地面以下
    if (particle.position.y < -5) {
      particle.active = false;
    }
  }
}

export default PhysicsEngine;
